/**
 * AdminSharePage
 * Export social-media images (1080×1920 story / 1080×1350 post / 1920×1080
 * landscape) of approved artworks — prompt + artist credit, set in the site's
 * type — plus the halftone prompt-themes chart, and an MP4 that moves through
 * a chosen set of drawings. Pick drawings, pick a format and style, export.
 */

import { useEffect, useMemo, useState } from 'react';
import { Download, Loader2, Check, Plus } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';
import {
  computePromptThemes,
  computePromptLengths,
  computeTopArtists,
  computeDrawnVsWaiting,
  computeTopWords,
  computeTimeToDraw,
  computeArtistDistribution,
  computePromptsByMonth,
  computeBusiestDay,
  computeReturning,
} from '@/utils/promptThemes';
import {
  exportArtworkImage,
  exportChartImage,
  type ShareArtwork,
  type ChartSpec,
  type SocialFormat,
  type SocialStyle,
} from '@/lib/socialExport';
import { recordStatsVideo, statsVideoSeconds } from '@/lib/statsVideo';
import { exportPromptsVideo } from '@/lib/promptsVideo';

const MONO = 'FK Grotesk Mono, monospace';
const DISPLAY = 'Delcan Mono, monospace';
const PAGE = 120; // drawings revealed per "See more"

// How the picker lays the drawings out: by date either way, by marks, or by person.
type PickOrder = 'newest' | 'oldest' | 'marked' | 'person';
const when = (a: any): number => {
  const t = new Date(a.createdAt).getTime();
  return Number.isFinite(t) ? t : 0;
};
const nameKey = (a: any) => String(a.artistName || '').trim().toLowerCase();
const monthOf = (a: any) => {
  const t = when(a);
  return t ? new Date(t).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Undated';
};

const FORMATS: { id: SocialFormat; label: string }[] = [
  { id: 'story', label: 'Story · 1080×1920' },
  { id: 'post', label: 'Post · 1080×1350' },
  { id: 'landscape', label: 'Landscape · 1920×1080' },
];
const STYLES: { id: SocialStyle; label: string }[] = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'black', label: 'Black' },
  { id: 'poster', label: 'Poster' },
];

function Chip({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`border rounded-full px-4 py-2 text-sm transition-colors ${
        active
          ? 'bg-foreground text-background border-foreground'
          : 'border-border text-foreground hover:bg-accent'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      style={{ fontFamily: MONO }}
    >
      {children}
    </button>
  );
}

export function AdminSharePage() {
  const { artworks, prompts } = useApp();
  const [query, setQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<SocialFormat>('story');
  const [style, setStyle] = useState<SocialStyle>('light');
  const [header, setHeader] = useState(false); // include the "Prompt-Brush 2.0 / .com" text — default off
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [shown, setShown] = useState(PAGE);
  const [order, setOrder] = useState<PickOrder>('newest');
  const [person, setPerson] = useState(''); // '' = everyone; otherwise a nameKey
  const [big, setBig] = useState(false); // larger tiles, fewer per row
  const [vidPct, setVidPct] = useState<number | null>(null);
  // The prompts video: how long each drawing holds, and whether it cuts (default) or dissolves.
  const [hold, setHold] = useState(3);
  const [dissolve, setDissolve] = useState(false);
  const [pvPct, setPvPct] = useState<number | null>(null);

  // A new search, order or person starts the grid over from the first page.
  useEffect(() => setShown(PAGE), [query, order, person]);

  const approved = useMemo(
    () => artworks.filter((a: any) => a.status === 'approved' || a.imageUrl || a.imageData),
    [artworks]
  );

  // Everyone who has drawn, most prolific first — the person menu.
  const people = useMemo(() => {
    const m = new Map<string, { label: string; n: number }>();
    approved.forEach((a: any) => {
      const key = nameKey(a);
      if (!key) return;
      const cur = m.get(key);
      if (cur) cur.n += 1;
      else m.set(key, { label: String(a.artistName).trim(), n: 1 });
    });
    return [...m.entries()]
      .map(([key, v]) => ({ key, ...v }))
      .sort((x, y) => y.n - x.n || x.label.localeCompare(y.label));
  }, [approved]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = person ? approved.filter((a: any) => nameKey(a) === person) : approved;
    if (q) {
      list = list.filter(
        (a: any) =>
          String(a.promptText || '').toLowerCase().includes(q) ||
          String(a.artistName || '').toLowerCase().includes(q) ||
          String(a.promptNumber || '').includes(q)
      );
    }
    const out = [...list];
    switch (order) {
      case 'oldest':
        out.sort((a: any, b: any) => when(a) - when(b));
        break;
      case 'marked':
        out.sort((a: any, b: any) => (b.heartCount || 0) - (a.heartCount || 0) || when(b) - when(a));
        break;
      case 'person':
        out.sort((a: any, b: any) => nameKey(a).localeCompare(nameKey(b)) || when(b) - when(a));
        break;
      default:
        out.sort((a: any, b: any) => when(b) - when(a));
    }
    return out;
  }, [approved, query, person, order]);

  const visible = useMemo(() => filtered.slice(0, shown), [filtered, shown]);

  // The grid in rows: a heading whenever the month (or the person) changes, then its drawings.
  const groupOf = (a: any) =>
    order === 'person' ? String(a.artistName || '').trim() || '—' : order === 'marked' ? '' : monthOf(a);
  const rows = useMemo(() => {
    const counts = new Map<string, number>();
    filtered.forEach((a: any) => {
      const g = groupOf(a);
      counts.set(g, (counts.get(g) || 0) + 1);
    });
    const out: ({ kind: 'head'; label: string; n: number } | { kind: 'item'; a: any })[] = [];
    let last: string | null = null;
    visible.forEach((a: any) => {
      const g = groupOf(a);
      if (g && g !== last) {
        out.push({ kind: 'head', label: g, n: counts.get(g) || 0 });
        last = g;
      }
      out.push({ kind: 'item', a });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, visible, order]);
  const selectedList = useMemo(
    () => approved.filter((a: any) => selectedIds.has(a.id)),
    [approved, selectedIds]
  );
  // The same drawings in the order they were picked — the order the video plays them.
  const pickedList = useMemo(() => {
    const byId = new Map(approved.map((a: any) => [a.id, a]));
    return [...selectedIds].map((id) => byId.get(id)).filter(Boolean);
  }, [approved, selectedIds]);

  const toggle = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visible.forEach((a: any) => next.add(a.id));
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());

  // Let the page choose: a random dozen from the most-marked drawings, or from all of them.
  const shuffle = <T,>(xs: T[]) => {
    const a = xs.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };
  const pickBest = (n: number) => {
    const ranked = [...approved].sort((a: any, b: any) => (b.heartCount || 0) - (a.heartCount || 0));
    const pool = ranked.slice(0, Math.max(n * 3, 40));
    setSelectedIds(new Set(shuffle(pool).slice(0, n).map((a: any) => a.id)));
  };
  const pickRandom = (n: number) => setSelectedIds(new Set(shuffle(approved).slice(0, n).map((a: any) => a.id)));

  const promptsN = prompts.length.toLocaleString();
  const drawingsN = approved.length.toLocaleString();
  const charts: ChartSpec[] = useMemo(
    () => [
      {
        slug: 'themes',
        kind: 'donut',
        title: 'What people ask us to draw',
        items: computePromptThemes(prompts as any[]),
        footnote: `Based on ${promptsN} prompts`,
      },
      {
        slug: 'prompt-length',
        kind: 'columns',
        title: 'How many words is a prompt?',
        items: computePromptLengths(prompts as any[]),
        footnote: `Based on ${promptsN} prompts`,
      },
      {
        slug: 'top-artists',
        kind: 'bars',
        title: 'Who has drawn the most',
        items: computeTopArtists(approved as any[]),
        footnote: `Based on ${drawingsN} drawings`,
      },
      {
        slug: 'top-words',
        kind: 'bars',
        title: 'The words you reach for most',
        items: computeTopWords(prompts as any[]),
        footnote: `Based on ${promptsN} prompts`,
      },
      {
        slug: 'drawn-vs-waiting',
        kind: 'donut',
        title: 'Drawn, or still waiting?',
        items: computeDrawnVsWaiting(prompts as any[], approved as any[]),
        footnote: `Based on ${promptsN} prompts`,
      },
      {
        slug: 'time-to-draw',
        kind: 'columns',
        title: 'How long until a prompt is drawn?',
        items: computeTimeToDraw(prompts as any[], approved as any[]).buckets,
        footnote: `Based on ${drawingsN} drawings`,
      },
      {
        slug: 'artist-distribution',
        kind: 'columns',
        title: 'How many drawings each artist makes',
        items: computeArtistDistribution(approved as any[]),
        footnote: `Based on ${drawingsN} drawings`,
      },
      {
        slug: 'returning',
        kind: 'columns',
        title: 'How many weeks people come back',
        items: computeReturning(approved as any[]).buckets,
        footnote: `Based on ${drawingsN} drawings`,
      },
      {
        slug: 'by-month',
        kind: 'columns',
        title: 'Prompts asked, by month',
        items: computePromptsByMonth(prompts as any[]),
        footnote: `Based on ${promptsN} prompts`,
      },
      {
        slug: 'busiest-day',
        kind: 'columns',
        title: 'When the community draws',
        items: computeBusiestDay(approved as any[]),
        footnote: `Based on ${drawingsN} drawings`,
      },
    ],
    [prompts, approved, promptsN, drawingsN]
  );

  // Poster style only makes sense in the tall story format.
  const effectiveStyle = format !== 'story' && style === 'poster' ? 'light' : style;

  const runExport = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    try {
      await fn();
      toast.success('Image exported');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  // Export every selected drawing, one at a time, with a short pause so the browser
  // doesn't drop rapid downloads. (For very large batches it'll ask to allow multiple
  // downloads once, then proceed.)
  const exportSelected = async () => {
    if (selectedList.length === 0 || busy) return;
    setBusy('artwork');
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < selectedList.length; i++) {
      const a: any = selectedList[i];
      setProgress({ done: i, total: selectedList.length });
      const art: ShareArtwork = {
        imageUrl: a.imageUrl,
        imageData: a.imageData,
        promptText: a.promptText,
        promptNumber: a.promptNumber,
        artistName: a.artistName,
      };
      try {
        await exportArtworkImage(art, format, effectiveStyle, { header });
        ok++;
      } catch {
        failed++;
      }
      await new Promise((r) => setTimeout(r, 400));
    }
    setProgress(null);
    setBusy(null);
    toast.success(`Exported ${ok} image${ok === 1 ? '' : 's'}${failed ? ` · ${failed} failed` : ''}`);
  };

  // The prompts video — the picked drawings, held and cut, in the chosen format and style.
  const exportPromptsVideoNow = async () => {
    if (busy || pickedList.length === 0) return;
    setBusy('pvideo');
    setPvPct(0);
    try {
      const arts: ShareArtwork[] = pickedList.map((a: any) => ({
        imageUrl: a.imageUrl,
        imageData: a.imageData,
        promptText: a.promptText,
        promptNumber: a.promptNumber,
        artistName: a.artistName,
      }));
      const ext = await exportPromptsVideo(arts, format, effectiveStyle, {
        hold,
        dissolve: dissolve ? 0.5 : 0,
        header,
        onProgress: (f) => setPvPct(Math.round(f * 100)),
      });
      toast.success(
        ext === 'mp4'
          ? `Video exported (MP4, ${pickedList.length} drawings)`
          : "Video exported (WebM — this browser can't encode MP4; Chrome and Safari can)"
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Video export failed');
    } finally {
      setBusy(null);
      setPvPct(null);
    }
  };

  // Render the charts animating into one video, in the format and style above.
  const exportVideo = async () => {
    if (busy) return;
    setBusy('video');
    setVidPct(0);
    try {
      const ext = await recordStatsVideo(charts, {
        format,
        style: effectiveStyle === 'poster' ? 'light' : effectiveStyle,
        header,
        onProgress: (f) => setVidPct(Math.round(f * 100)),
      });
      toast.success(
        ext === 'mp4'
          ? 'Stats video exported (MP4)'
          : "Stats video exported (WebM — this browser can't encode MP4; Chrome and Safari can)"
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Video export failed');
    } finally {
      setBusy(null);
      setVidPct(null);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-foreground text-2xl mb-2" style={{ fontFamily: DISPLAY }}>
          Share images
        </h1>
        <p className="text-muted-foreground mb-10" style={{ fontFamily: MONO, fontSize: '10.5pt' }}>
          Export drawings and stats as PNGs for social — typeset like the site.
        </p>

        {/* Format + style pickers */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          {FORMATS.map((f) => (
            <Chip key={f.id} active={format === f.id} onClick={() => setFormat(f.id)}>
              {f.label}
            </Chip>
          ))}
          <span className="w-4" />
          {STYLES.map((s) => (
            <Chip
              key={s.id}
              active={effectiveStyle === s.id}
              disabled={s.id === 'poster' && format !== 'story'}
              onClick={() => setStyle(s.id)}
            >
              {s.label}
            </Chip>
          ))}
          <span className="w-4" />
          <Chip active={header} onClick={() => setHeader((h) => !h)}>
            {header ? '✓ ' : ''}Prompt-Brush header
          </Chip>
        </div>

        {/* Multi-select action bar */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            onClick={exportSelected}
            disabled={selectedList.length === 0 || busy !== null}
            className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-6 py-3 disabled:opacity-40"
            style={{ fontFamily: MONO, fontSize: '11pt' }}
          >
            {busy === 'artwork' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {busy === 'artwork' && progress
              ? `Exporting ${progress.done + 1}/${progress.total}…`
              : selectedList.length === 0
                ? 'Select drawings below'
                : `Export ${selectedList.length} image${selectedList.length === 1 ? '' : 's'}`}
          </button>
          <button
            onClick={selectAllVisible}
            disabled={busy !== null}
            className="border border-border rounded-full px-4 py-3 text-foreground hover:bg-accent disabled:opacity-40"
            style={{ fontFamily: MONO, fontSize: '10.5pt' }}
          >
            Select all{query.trim() || person ? ' shown' : ''}
          </button>
          {selectedList.length > 0 && (
            <button
              onClick={clearSelection}
              disabled={busy !== null}
              className="border border-border rounded-full px-4 py-3 text-foreground hover:bg-accent disabled:opacity-40"
              style={{ fontFamily: MONO, fontSize: '10.5pt' }}
            >
              Clear ({selectedList.length})
            </button>
          )}
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search prompt, artist, or #"
            className="border border-border rounded-full px-5 py-3 bg-card text-foreground w-64 ml-auto"
            style={{ fontFamily: MONO, fontSize: '10.5pt' }}
          />
        </div>

        {/* Order, person, size */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <span className="text-muted-foreground mr-1" style={{ fontFamily: MONO, fontSize: '9.5pt' }}>
            Order
          </span>
          <Chip active={order === 'newest'} onClick={() => setOrder('newest')}>
            Newest
          </Chip>
          <Chip active={order === 'oldest'} onClick={() => setOrder('oldest')}>
            Oldest
          </Chip>
          <Chip active={order === 'marked'} onClick={() => setOrder('marked')}>
            Most marked
          </Chip>
          <Chip active={order === 'person'} onClick={() => setOrder('person')}>
            By person
          </Chip>
          <select
            value={person}
            onChange={(e) => setPerson(e.target.value)}
            aria-label="Show one person's drawings"
            className="border border-border rounded-full px-4 py-2 bg-card text-foreground ml-2"
            style={{ fontFamily: MONO, fontSize: '10pt' }}
          >
            <option value="">Everyone · {approved.length}</option>
            {people.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label} · {p.n}
              </option>
            ))}
          </select>
          <span className="ml-auto" />
          <Chip active={big} onClick={() => setBig((b) => !b)}>
            Bigger
          </Chip>
        </div>

        {/* Artwork grid — tap to toggle selection */}
        <div
          className={`grid gap-4 mb-8 ${
            big ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'
          }`}
        >
          {rows.map((row) => {
            if (row.kind === 'head') {
              return (
                <div
                  key={`h-${row.label}`}
                  className="col-span-full flex items-baseline gap-3 pt-6 pb-1 border-b border-border"
                >
                  <span className="text-foreground" style={{ fontFamily: DISPLAY, fontSize: '13pt' }}>
                    {row.label}
                  </span>
                  <span className="text-muted-foreground" style={{ fontFamily: MONO, fontSize: '9pt' }}>
                    {row.n} drawing{row.n === 1 ? '' : 's'}
                  </span>
                </div>
              );
            }
            const a = row.a;
            const isSel = selectedIds.has(a.id);
            return (
            <button
              key={a.id}
              onClick={() => toggle(a.id)}
              className={`relative text-left rounded-lg overflow-hidden border-2 transition-colors ${
                isSel ? 'border-foreground' : 'border-transparent hover:border-border'
              }`}
            >
              <span
                className={`absolute top-1.5 right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                  isSel
                    ? 'bg-foreground text-background border-foreground'
                    : 'bg-background/70 border-border text-transparent'
                }`}
              >
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
              <img
                src={a.imageUrl || a.imageData}
                alt={a.promptText || 'drawing'}
                loading="lazy"
                className={`w-full object-cover bg-card transition-opacity ${isSel ? '' : 'opacity-100'}`}
                style={{ aspectRatio: '5/7' }}
              />
              <div className="px-1.5 py-1.5">
                <div
                  className="text-foreground truncate"
                  style={{ fontFamily: MONO, fontSize: '8.5pt' }}
                >
                  {a.promptText || 'Untitled'}
                </div>
                <div
                  className="text-muted-foreground truncate uppercase"
                  style={{ fontFamily: MONO, fontSize: '7.5pt' }}
                >
                  {a.artistName || '—'}
                </div>
              </div>
            </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-muted-foreground col-span-full" style={{ fontFamily: MONO }}>
              No drawings match.
            </p>
          )}
        </div>

        {/* See more pager */}
        {filtered.length > visible.length && (
          <div className="flex flex-col items-center gap-2 mb-16">
            <button
              onClick={() => setShown((n) => n + PAGE)}
              className="inline-flex items-center gap-2 border border-border rounded-full px-7 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              <Plus className="w-4 h-4" />
              See more
            </button>
            <span className="text-muted-foreground" style={{ fontFamily: MONO, fontSize: '9pt' }}>
              Showing {visible.length} of {filtered.length}
            </span>
          </div>
        )}

        {/* Prompts video — a clip that moves through the selected drawings */}
        <h2 className="text-foreground text-xl mt-16 mb-2" style={{ fontFamily: DISPLAY }}>
          Prompts video
        </h2>
        <p className="text-muted-foreground mb-6 max-w-2xl" style={{ fontFamily: MONO, fontSize: '10.5pt' }}>
          An MP4 that moves through the drawings selected above, in the order you picked them,
          each set like its share image in the format and style chosen at the top. Pick them by
          hand, or let it choose.
        </p>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Chip active={false} disabled={busy !== null} onClick={() => pickBest(12)}>
            Random 12 of the most marked
          </Chip>
          <Chip active={false} disabled={busy !== null} onClick={() => pickRandom(12)}>
            Random 12
          </Chip>
          <span className="w-4" />
          {[1, 2, 3, 4].map((sec) => (
            <Chip key={sec} active={hold === sec} onClick={() => setHold(sec)}>
              {sec}s each
            </Chip>
          ))}
          <span className="w-4" />
          <Chip active={!dissolve} onClick={() => setDissolve(false)}>
            Cut
          </Chip>
          <Chip active={dissolve} onClick={() => setDissolve(true)}>
            Dissolve
          </Chip>
        </div>
        <div className="flex flex-wrap items-center gap-4 mb-16">
          <button
            onClick={exportPromptsVideoNow}
            disabled={pickedList.length === 0 || busy !== null}
            className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-6 py-3 disabled:opacity-40"
            style={{ fontFamily: MONO, fontSize: '11pt' }}
          >
            {busy === 'pvideo' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {busy === 'pvideo'
              ? `Rendering… ${pvPct ?? 0}%`
              : pickedList.length === 0
                ? 'Select drawings above'
                : `Export video · ${pickedList.length} drawing${pickedList.length === 1 ? '' : 's'} · ${
                    FORMATS.find((f) => f.id === format)?.label.split(' · ')[1]
                  }`}
          </button>
          <span className="text-muted-foreground" style={{ fontFamily: MONO, fontSize: '9.5pt' }}>
            {pickedList.length > 0
              ? `About ${Math.round(pickedList.length * hold + (dissolve ? (pickedList.length - 1) * 0.5 : 0))}s. `
              : ''}
            Renders in the browser — MP4 in Chrome and Safari, no need to keep the tab in front.
          </span>
        </div>
        <div className="mb-16" />

        {/* Stats charts */}
        <h2 className="text-foreground text-xl mb-2" style={{ fontFamily: DISPLAY }}>
          Stats charts
        </h2>
        <p className="text-muted-foreground mb-6" style={{ fontFamily: MONO, fontSize: '10.5pt' }}>
          The halftone graphs from the About page, sized for social (uses the format + style above; charts ignore “poster”).
        </p>
        <div className="flex flex-wrap gap-3">
          {charts.map((c) => (
            <button
              key={c.slug}
              onClick={() =>
                runExport(`chart-${c.slug}`, () =>
                  exportChartImage(c, format, effectiveStyle === 'poster' ? 'light' : effectiveStyle, {
                    header,
                  })
                )
              }
              disabled={busy !== null || c.items.length === 0}
              className="inline-flex items-center gap-2 border border-border rounded-full px-6 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors disabled:opacity-40"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              {busy === `chart-${c.slug}` ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              {c.title}
            </button>
          ))}
        </div>

        {/* Animated stats video */}
        <h2 className="text-foreground text-xl mt-16 mb-2" style={{ fontFamily: DISPLAY }}>
          Animated stats video
        </h2>
        <p className="text-muted-foreground mb-6" style={{ fontFamily: MONO, fontSize: '10.5pt' }}>
          Every chart animating in turn, in the format and style above (charts ignore “poster”) —
          about {Math.round(statsVideoSeconds(charts.length))}s of video. Renders in the browser: MP4
          in Chrome and Safari, no need to keep the tab in front.
        </p>
        <button
          onClick={exportVideo}
          disabled={busy !== null}
          className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-6 py-3 disabled:opacity-40"
          style={{ fontFamily: MONO, fontSize: '11pt' }}
        >
          {busy === 'video' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          {busy === 'video'
            ? `Rendering… ${vidPct ?? 0}%`
            : `Export animated video · ${FORMATS.find((f) => f.id === format)?.label.split(' · ')[1]}`}
        </button>
      </div>
    </div>
  );
}
